import { RecordFields, RecordList } from "@/components/common/RecordList";
import { RecordSortControl } from "@/components/common/RecordSortControl";
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Inventory as InventoryIcon,
  Phonelink as MobileLinksIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  FormControlLabel,
  Switch,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AdminEmptyState,
  AdminFilterSummary,
  AdminTableActions,
  AdminTablePanel,
  AuditActor,
  PageShell,
} from "@/components/common";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import MobileLinkAuthToken from "@/components/features/MobileLink/MobileLinkAuthToken";
import MobileLinkForm from "@/components/features/MobileLink/MobileLinkForm";
import { getApiConfig } from "@/config/api.config";
import { useLocalizedTablePagination } from "@/hooks/useLocalizedTablePagination";
import { useTableSorting } from "@/hooks/useTableSorting";
import { Customer, CustomerService } from "@/services/features/CustomerService";
import MobileLinkService from "@/services/features/MobileLinkService";
import { MobileLink } from "@/types/mobileLink";
import { getAuditActorName } from "@/utils/auditActor";
import { formatStoreDateTimeWithTimezone, getBrowserTimezone } from "@/utils/storeTime";

const MobileLinks: React.FC = () => {
  const { t, i18n } = useTranslation();
  const customerService = useMemo(() => CustomerService.getInstance(), []);
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);

  const [showDisabled, setShowDisabled] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const localizedPagination = useLocalizedTablePagination();

  const [state, setState] = useState({
    loading: {
      isLoadingInitial: true,
      isLoadingMobileLinks: false,
    },
    error: null as string | null,
    data: {
      customers: [] as Customer[],
      mobileLinks: [] as MobileLink[],
    },
    selection: {
      selectedCustomer: null as Customer | null,
    },
    form: {
      // New state for form management
      isOpen: false,
      selectedMobileLink: null as MobileLink | null,
    },
  });
  const locale = i18n.resolvedLanguage || i18n.language || "en-US";
  const visibleLinks = useMemo(
    () => state.data.mobileLinks.filter((link) => showDisabled || link.enabled),
    [state.data.mobileLinks, showDisabled],
  );
  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting(
    visibleLinks,
    (link, column) =>
      ({
        id: link.id,
        name: link.name,
        storeId: link.storeId,
        authorizationId: link.authorizationId,
        enabled: link.enabled,
        createdAt: link.createdAt ? new Date(link.createdAt).getTime() : null,
        createdBy: getAuditActorName(link.createdBy),
      })[column],
    "id" as "id" | "name" | "storeId" | "authorizationId" | "enabled" | "createdAt" | "createdBy",
    locale,
  );

  useEffect(() => {
    setPage(0);
  }, [showDisabled, state.selection.selectedCustomer?.id, sortColumn, sortDirection]);

  const currentPage = Math.min(page, Math.max(0, Math.ceil(sortedItems.length / rowsPerPage) - 1));
  const paginatedLinks = sortedItems.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage,
  );
  const renderPagination = () => (
    <TablePagination
      component="div"
      count={visibleLinks.length}
      page={currentPage}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={[25, 50, 100, 250]}
      onPageChange={(_, nextPage) => setPage(nextPage)}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
      }}
      {...localizedPagination}
    />
  );

  const updateState = useCallback((updater: (prevState: typeof state) => typeof state) => {
    setState((prev) => updater(prev));
  }, []);

  useEffect(() => {
    document.title = t("management.mobileLink.mobileLinks.documentTitle");
    const fetchInitialData = async () => {
      try {
        updateState((prev) => ({
          ...prev,
          loading: { ...prev.loading, isLoadingInitial: true },
        }));
        const customersResponse = await customerService.getAllCustomers();
        if (customersResponse.success) {
          updateState((prev) => ({
            ...prev,
            data: {
              ...prev.data,
              customers: customersResponse.data || [],
            },
            loading: { ...prev.loading, isLoadingInitial: false },
          }));
        } else {
          updateState((prev) => ({
            ...prev,
            error:
              customersResponse.error ||
              t("management.mobileLink.mobileLinks.errors.failedFetchCustomers"),
            loading: { ...prev.loading, isLoadingInitial: false },
          }));
        }
      } catch (err) {
        updateState((prev) => ({
          ...prev,
          error:
            err instanceof Error
              ? err.message
              : t("management.mobileLink.mobileLinks.errors.unexpectedFetchCustomers"),
          loading: { ...prev.loading, isLoadingInitial: false },
        }));
      }
    };
    fetchInitialData();
  }, [customerService, t, updateState]);

  useEffect(() => {
    if (state.selection.selectedCustomer) {
      fetchMobileLinks(state.selection.selectedCustomer.id);
    } else {
      updateState((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          mobileLinks: [],
        },
      }));
    }
  }, [state.selection.selectedCustomer, updateState]);

  const fetchMobileLinks = async (customerId: number) => {
    updateState((prev) => ({
      ...prev,
      loading: { ...prev.loading, isLoadingMobileLinks: true },
      error: null,
    }));
    try {
      const links = await MobileLinkService.getByCustomerId(customerId);
      updateState((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          mobileLinks: links,
        },
      }));
    } catch (err) {
      updateState((prev) => ({
        ...prev,
        error:
          err instanceof Error
            ? err.message
            : t("management.mobileLink.mobileLinks.errors.unexpectedFetchMobileLinks"),
      }));
    } finally {
      updateState((prev) => ({
        ...prev,
        loading: { ...prev.loading, isLoadingMobileLinks: false },
      }));
    }
  };

  const handleCustomerSelect = useCallback(
    (customer: Customer | null) => {
      updateState((prev) => ({
        ...prev,
        selection: {
          ...prev.selection,
          selectedCustomer: customer,
        },
      }));
    },
    [updateState],
  );

  const handleOpenCreateForm = useCallback(() => {
    updateState((prev) => ({
      ...prev,
      form: {
        isOpen: true,
        selectedMobileLink: null,
      },
    }));
  }, [updateState]);

  const handleOpenEditForm = useCallback(
    (mobileLink: MobileLink) => {
      updateState((prev) => ({
        ...prev,
        form: {
          isOpen: true,
          selectedMobileLink: mobileLink,
        },
      }));
    },
    [updateState],
  );

  const handleCloseForm = useCallback(() => {
    updateState((prev) => ({
      ...prev,
      form: {
        isOpen: false,
        selectedMobileLink: null,
      },
    }));
  }, [updateState]);

  const handleSaveForm = useCallback(() => {
    if (state.selection.selectedCustomer) {
      fetchMobileLinks(state.selection.selectedCustomer.id);
    }
  }, [state.selection.selectedCustomer, fetchMobileLinks]);

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.mobileLink.mobileLinks.title")}
      icon={<MobileLinksIcon />}
      headerActions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateForm}
          disabled={!state.selection.selectedCustomer || state.loading.isLoadingMobileLinks}
        >
          {t("management.mobileLink.mobileLinks.actions.createNew")}
        </Button>
      }
    >
      <div className="w-full space-y-6">
        <AdminFilterSummary
          filter={
            <Box sx={{ width: "100%" }}>
              <MuiAutocompleteMultiSelect
                label={t("management.mobileLink.mobileLinks.fields.customer")}
                options={state.data.customers}
                value={state.selection.selectedCustomer ? [state.selection.selectedCustomer] : []}
                onChange={(next) => handleCustomerSelect(next[0] ?? null)}
                getOptionLabel={(customer) => customer.name}
                getOptionKey={(customer) => customer.id}
                disabled={state.loading.isLoadingInitial}
                loading={state.loading.isLoadingInitial}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "56px",
                  },
                }}
              />
            </Box>
          }
          count={visibleLinks.length}
          countLabel={t("management.mobileLink.mobileLinks.fields.totalMobileLinks")}
          icon={<MobileLinksIcon color="primary" sx={{ fontSize: 40 }} />}
        />

        {state.error && (
          <Card sx={{ p: 4, textAlign: "center" }}>
            <Typography color="error">{state.error}</Typography>
          </Card>
        )}

        <AdminTablePanel
          header={
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={showDisabled}
                    onChange={(event) => {
                      setShowDisabled(event.target.checked);
                      if (!event.target.checked && sortColumn === "enabled") handleSort("id");
                    }}
                  />
                }
                label={t("management.mobileLink.mobileLinks.actions.showDisabled")}
              />
              {renderPagination()}
            </Box>
          }
          footer={renderPagination()}

          mobileContent={
            <Box
              sx={{
                minWidth: 0,
                "& .MuiTypography-root": {
                  maxWidth: "100%",
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                },
              }}
            >
              <RecordSortControl
                value={sortColumn}
                direction={sortDirection}
                onSort={handleSort}
                options={[
                  { value: "id" as const, label: t("management.mobileLink.mobileLinks.fields.id") },
                  ...(showDisabled
                    ? [
                        {
                          value: "enabled" as const,
                          label: t("management.mobileLink.mobileLinks.fields.enabled"),
                        },
                      ]
                    : []),
                  {
                    value: "name" as const,
                    label: t("management.mobileLink.mobileLinks.fields.name"),
                  },
                  {
                    value: "storeId" as const,
                    label: t("management.mobileLink.mobileLinks.fields.storeId"),
                  },
                  {
                    value: "authorizationId" as const,
                    label: t("management.mobileLink.mobileLinks.fields.authorizationId"),
                  },
                  {
                    value: "createdAt" as const,
                    label: t("management.mobileLink.mobileLinks.fields.createdAt"),
                  },
                  {
                    value: "createdBy" as const,
                    label: t("management.mobileLink.mobileLinks.fields.createdBy"),
                  },
                ]}
              />
              <RecordList
                items={paginatedLinks}
                getItemId={(link) => link.id}
                loading={state.loading.isLoadingMobileLinks}
                resetKey={JSON.stringify([
                  sortColumn,
                  sortDirection,
                  ...paginatedLinks.map((link) => link.id),
                ])}
                renderTitle={(link) => {
                  return <>{link.name}</>;
                }}
                renderSummary={(link) => {
                  return (
                    <RecordFields
                      fields={[
                        {
                          label: <>{t("management.mobileLink.mobileLinks.fields.id")}</>,
                          value: <>{link.id}</>,
                        },
                        {
                          label: <>{t("management.mobileLink.mobileLinks.fields.storeId")}</>,
                          value: <>{link.storeId}</>,
                        },
                      ]}
                    />
                  );
                }}
                renderDetails={(link) => {
                  return (
                    <RecordFields
                      fields={[
                        ...(showDisabled
                          ? [
                              {
                                label: <>{t("management.mobileLink.mobileLinks.fields.enabled")}</>,
                                value: (
                                  <>
                                    <Tooltip
                                      title={t(
                                        link.enabled
                                          ? "management.webhooks.webhooks.status.enabled"
                                          : "management.webhooks.webhooks.status.disabled",
                                      )}
                                    >
                                      {link.enabled ? (
                                        <CheckCircleIcon
                                          color="success"
                                          fontSize="small"
                                          role="img"
                                          titleAccess={t(
                                            "management.webhooks.webhooks.status.enabled",
                                          )}
                                        />
                                      ) : (
                                        <CancelIcon
                                          color="error"
                                          fontSize="small"
                                          role="img"
                                          titleAccess={t(
                                            "management.webhooks.webhooks.status.disabled",
                                          )}
                                        />
                                      )}
                                    </Tooltip>
                                  </>
                                ),
                              },
                            ]
                          : []),
                        {
                          label: (
                            <>{t("management.mobileLink.mobileLinks.fields.authorizationId")}</>
                          ),
                          value: (
                            <>
                              <MobileLinkAuthToken
                                authToken={link.authorizationId}
                                linkId={link.id}
                                storeId={link.storeId}
                                linkName={link.name}
                                selectedCustomerId={state.selection.selectedCustomer?.id ?? null}
                              />
                            </>
                          ),
                        },
                        {
                          label: <>{t("management.mobileLink.mobileLinks.fields.createdAt")}</>,
                          value: (
                            <>
                              {formatStoreDateTimeWithTimezone(
                                link.createdAt,
                                browserTimezone,
                                locale,
                              )}
                            </>
                          ),
                        },
                        {
                          label: <>{t("management.mobileLink.mobileLinks.fields.createdBy")}</>,
                          value: (
                            <>
                              <AuditActor value={link.createdBy} />
                            </>
                          ),
                        },
                      ]}
                    />
                  );
                }}
                renderActions={(link) => (
                  <AdminTableActions
                    actions={[
                      {
                        icon: <EditIcon />,
                        hint: t("management.mobileLink.mobileLinks.actions.edit"),
                        action: () => handleOpenEditForm(link),
                      },
                      {
                        icon: <InventoryIcon />,
                        hint: t("management.stores.stores.tooltips.viewInventory"),
                        action: () => {
                          window.open(
                            `${getApiConfig().inventory}/sample/${link.storeId}`,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        },
                      },
                    ]}
                  />
                )}

                emptyState={
                  <AdminEmptyState
                    icon={<MobileLinksIcon sx={{ fontSize: 40 }} />}
                    text={
                      <>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          {t("management.mobileLink.mobileLinks.empty.selectCustomer")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("management.mobileLink.mobileLinks.empty.customerAssociation")}
                        </Typography>
                      </>
                    }
                  />
                }
              />
            </Box>
          }
        >
          <TableHead>
            <TableRow>
              <SortableTableCell
                activeColumn={sortColumn}
                column="id"
                direction={sortDirection}
                onSort={handleSort}
              >
                {t("management.mobileLink.mobileLinks.fields.id")}
              </SortableTableCell>
              {showDisabled && (
                <SortableTableCell
                  activeColumn={sortColumn}
                  column="enabled"
                  direction={sortDirection}
                  onSort={handleSort}
                >
                  {t("management.mobileLink.mobileLinks.fields.enabled")}
                </SortableTableCell>
              )}
              <SortableTableCell
                activeColumn={sortColumn}
                column="name"
                direction={sortDirection}
                onSort={handleSort}
              >
                {t("management.mobileLink.mobileLinks.fields.name")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={sortColumn}
                column="storeId"
                direction={sortDirection}
                onSort={handleSort}
              >
                {t("management.mobileLink.mobileLinks.fields.storeId")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={sortColumn}
                column="authorizationId"
                direction={sortDirection}
                onSort={handleSort}
              >
                {t("management.mobileLink.mobileLinks.fields.authorizationId")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={sortColumn}
                column="createdAt"
                direction={sortDirection}
                onSort={handleSort}
              >
                {t("management.mobileLink.mobileLinks.fields.createdAt")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={sortColumn}
                column="createdBy"
                direction={sortDirection}
                onSort={handleSort}
              >
                {t("management.mobileLink.mobileLinks.fields.createdBy")}
              </SortableTableCell>
              <TableCell align="right">
                {t("management.mobileLink.mobileLinks.fields.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.loading.isLoadingMobileLinks ? (
              <TableRow>
                <TableCell colSpan={showDisabled ? 8 : 7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : !state.selection.selectedCustomer ? (
              <TableRow>
                <TableCell colSpan={showDisabled ? 8 : 7} align="center">
                  <AdminEmptyState
                    icon={<MobileLinksIcon sx={{ fontSize: 40 }} />}
                    text={
                      <>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          {t("management.mobileLink.mobileLinks.empty.selectCustomer")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("management.mobileLink.mobileLinks.empty.customerAssociation")}
                        </Typography>
                      </>
                    }
                  />
                </TableCell>
              </TableRow>
            ) : visibleLinks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showDisabled ? 8 : 7} align="center">
                  <AdminEmptyState
                    icon={<MobileLinksIcon sx={{ fontSize: 40 }} />}
                    text={t("management.mobileLink.mobileLinks.empty.noMobileLinks")}
                  />
                </TableCell>
              </TableRow>
            ) : (
              paginatedLinks.map((link) => (
                <TableRow key={link.id}>
                  <TableCell>{link.id}</TableCell>
                  {showDisabled && (
                    <TableCell>
                      <Tooltip
                        title={t(
                          link.enabled
                            ? "management.webhooks.webhooks.status.enabled"
                            : "management.webhooks.webhooks.status.disabled",
                        )}
                      >
                        {link.enabled ? (
                          <CheckCircleIcon
                            color="success"
                            fontSize="small"
                            role="img"
                            titleAccess={t("management.webhooks.webhooks.status.enabled")}
                          />
                        ) : (
                          <CancelIcon
                            color="error"
                            fontSize="small"
                            role="img"
                            titleAccess={t("management.webhooks.webhooks.status.disabled")}
                          />
                        )}
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell>{link.name}</TableCell>
                  <TableCell>{link.storeId}</TableCell>
                  <TableCell>
                    <MobileLinkAuthToken
                      authToken={link.authorizationId}
                      linkId={link.id}
                      storeId={link.storeId}
                      linkName={link.name}
                      selectedCustomerId={state.selection.selectedCustomer?.id ?? null}
                    />
                  </TableCell>
                  <TableCell>
                    {formatStoreDateTimeWithTimezone(link.createdAt, browserTimezone, locale)}
                  </TableCell>
                  <TableCell>
                    <AuditActor value={link.createdBy} />
                  </TableCell>
                  <TableCell align="right">
                    <AdminTableActions
                      actions={[
                        {
                          icon: <EditIcon />,
                          hint: t("management.mobileLink.mobileLinks.actions.edit"),
                          action: () => handleOpenEditForm(link),
                        },
                        {
                          icon: <InventoryIcon />,
                          hint: t("management.stores.stores.tooltips.viewInventory"),
                          action: () => {
                            window.open(
                              `${getApiConfig().inventory}/sample/${link.storeId}`,
                              "_blank",
                              "noopener,noreferrer",
                            );
                          },
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </AdminTablePanel>
      </div>
      {state.selection.selectedCustomer && (
        <MobileLinkForm
          open={state.form.isOpen}
          onClose={handleCloseForm}
          mobileLink={state.form.selectedMobileLink}
          customerId={state.selection.selectedCustomer.id}
          onSave={handleSaveForm}
        />
      )}
    </PageShell>
  );
};

export default MobileLinks;
